import Clarity from '@microsoft/clarity';
import { useEffect, useRef } from 'react';

import { isStartupableStarted } from '@/services/base/StartupHelper';
import TelemetryService from '@/services/telemetry/TelemetryService';

/**
 * ClarityManager - Microsoft Clarity analytics management component
 *
 * Manages the complete lifecycle of Microsoft Clarity analytics,
 * including initialization and consent signal management
 *
 * - Initializes Microsoft Clarity once on component mount
 * - Manages consent signals based on user cookie preferences
 * - Ensures compliance with EEA/UK/Switzerland consent requirements (effective Oct 31, 2025)
 *
 * @returns {null} This component doesn't render any UI elements
 */
const ClarityManager = () => {
  // telemetry service initialized only if consent already provided
  const isTelemetryConsentGiven = isStartupableStarted(TelemetryService);
  const isInitialized = useRef(false);

  useEffect(() => {
    const msClarityId = import.meta.env.VITE_MS_CLARITY_ID;
    if (!msClarityId) {
      console.error('Missing MS Clarity ID - ensure VITE_MS_CLARITY_ID is set');
      return;
    }

    if (!isInitialized.current) {
      try {
        Clarity.init(msClarityId);
        isInitialized.current = true;
      } catch (error) {
        console.error('Error initializing Clarity:', error);
      }
    }
  }, []);

  useEffect(() => {
    if (isInitialized.current) {
      try {
        // Set consent signal for GDPR compliance
        // true = full analytics features enabled
        // false = limited functionality, respects user privacy
        Clarity.consent(isTelemetryConsentGiven);
      } catch (error) {
        console.error('Error updating Clarity consent:', error);
      }
    }
  }, [isTelemetryConsentGiven]);

  return null;
};

export default ClarityManager;
